import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, BellRing, Send, RefreshCw, Smartphone } from "lucide-react";
import { notifStatus, requestNotifPermission, sendTestNotification, syncPlan } from "@/lib/notifications";

const FREQ = [
  { v: "normal", l: "Normal" },
  { v: "reduced", l: "Reduced (fewer)" },
  { v: "minimal", l: "Minimal (essential only)" },
  { v: "off", l: "Off" },
];
const CATS = [
  { k: "water", l: "Water reminders" },
  { k: "meal", l: "Meal check-ins" },
  { k: "symptom_followups", l: "Symptom follow-ups" },
  { k: "re_engagement", l: "Come-back nudges" },
  { k: "reminders", l: "Streak reminders" },
];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? "am" : "pm"}`;

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ native: false, permission: "unavailable" });
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/notifications/preferences"); setPrefs(data); }
    catch (_e) {
      // Render with sensible defaults rather than an infinite spinner if the fetch fails.
      setPrefs({ enabled: true, frequency: "normal", quiet_hours_start: 22, quiet_hours_end: 8, categories: {} });
    }
    try { setStatus(await notifStatus()); } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const setCat = (k, v) => setPrefs((p) => ({ ...p, categories: { ...(p.categories || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notifications/preferences", {
        enabled: prefs.enabled,
        frequency: prefs.frequency,
        quiet_hours_start: prefs.quiet_hours_start,
        quiet_hours_end: prefs.quiet_hours_end,
        categories: prefs.categories,
      });
      setPrefs(data);
      toast.success("Notification preferences saved");
      if (status.native) await syncPlan();
    } catch (_e) { toast.error("Couldn't save preferences"); }
    finally { setSaving(false); }
  };

  const onEnable = async () => {
    setBusy("perm");
    const res = await requestNotifPermission();
    setStatus(await notifStatus());
    if (res === "granted") toast.success("Notifications enabled");
    else if (res === "unavailable") toast.info("Open the KAAYA app to enable notifications");
    else toast.error("Permission not granted — enable it in your phone's app settings");
    setBusy("");
  };

  const onTest = async () => {
    setBusy("test");
    const res = await sendTestNotification();
    if (res.ok) toast.success("Test sent — in ~10s, tap “Log 250ml” to log a glass of water");
    else if (res.reason === "not_native") toast.info("Test notifications only work inside the KAAYA app");
    else toast.error("Couldn't send — check notification permission in app settings");
    setBusy("");
  };

  const onSync = async () => {
    setBusy("sync");
    const res = await syncPlan();
    if (res?.native === false) toast.info("Reminders are delivered in the KAAYA app — open it on your phone");
    else if (!res || res.ok === false) toast.error("Sync failed — try again");
    else if (res.suppressed) toast.info("No reminders due right now (quiet hours, caps, or nothing pending)");
    else toast.success(`${res.scheduled} reminder${res.scheduled === 1 ? "" : "s"} scheduled`);
    setBusy("");
  };

  if (!prefs) {
    return (
      <div className="bg-surface-elevated border border-border rounded-3xl p-6 text-sm text-muted-foreground" data-testid="notif-settings-loading">
        Loading notification settings…
      </div>
    );
  }

  const permBadge = status.permission === "granted"
    ? { t: "Enabled", c: "bg-secondary/15 text-secondary" }
    : status.native
      ? { t: "Not enabled", c: "bg-destructive/10 text-destructive" }
      : { t: "App only", c: "bg-muted text-foreground/60" };

  return (
    <div className="bg-surface-elevated border border-border rounded-3xl p-6" data-testid="notif-settings-card">
      <div className="flex items-center justify-between mb-1">
        <div className="text-overline flex items-center gap-1"><BellRing size={12} /> notifications</div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${permBadge.c}`} data-testid="notif-perm-badge">{permBadge.t}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Gentle reminders to log water, check in on meals, and follow up on how you're feeling. Reminders are delivered inside the KAAYA mobile app.
      </p>

      {/* Test / device panel */}
      <div className="bg-surface rounded-2xl p-4 border border-border mb-5" data-testid="notif-test-panel">
        {!status.native && (
          <div className="flex items-start gap-2 text-xs text-foreground/70 mb-3">
            <Smartphone size={14} className="text-secondary shrink-0 mt-0.5" />
            <span>You're on the web. Open the KAAYA app on your phone to receive and test reminders. Your preferences below still save.</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={onEnable} disabled={busy === "perm" || status.permission === "granted"} data-testid="notif-enable-btn">
            <Bell size={14} className="mr-1" /> {status.permission === "granted" ? "Enabled" : busy === "perm" ? "Enabling…" : "Enable notifications"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={onTest} disabled={busy === "test"} data-testid="notif-test-btn">
            <Send size={14} className="mr-1" /> {busy === "test" ? "Sending…" : "Send test (10s)"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={onSync} disabled={busy === "sync"} data-testid="notif-sync-btn">
            <RefreshCw size={14} className={`mr-1 ${busy === "sync" ? "animate-spin" : ""}`} /> Sync reminders now
          </Button>
        </div>
      </div>

      {/* Preferences */}
      <div className="flex items-center justify-between py-2 border-b border-border">
        <div><Label className="text-sm">All notifications</Label><p className="text-xs text-muted-foreground">Master switch for every reminder</p></div>
        <Switch checked={!!prefs.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="notif-enabled-switch" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div>
          <Label className="text-xs">Frequency</Label>
          <Select value={prefs.frequency || "normal"} onValueChange={(v) => set("frequency", v)} disabled={!prefs.enabled}>
            <SelectTrigger data-testid="notif-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>{FREQ.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Quiet from</Label>
            <Select value={String(prefs.quiet_hours_start)} onValueChange={(v) => set("quiet_hours_start", Number(v))} disabled={!prefs.enabled}>
              <SelectTrigger data-testid="notif-quiet-start"><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Quiet until</Label>
            <Select value={String(prefs.quiet_hours_end)} onValueChange={(v) => set("quiet_hours_end", Number(v))} disabled={!prefs.enabled}>
              <SelectTrigger data-testid="notif-quiet-end"><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-overline mb-2">what to remind me about</div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
          {CATS.map((c) => (
            <div key={c.k} className="flex items-center justify-between py-1.5">
              <Label className="text-sm font-normal text-foreground/80">{c.l}</Label>
              <Switch
                checked={prefs.categories?.[c.k] !== false}
                onCheckedChange={(v) => setCat(c.k, v)}
                disabled={!prefs.enabled}
                data-testid={`notif-cat-${c.k}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end mt-5">
        <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="notif-save-btn">
          {saving ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </div>
  );
}
